import { createContext, useCallback, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Navigate, useLocation } from 'react-router';
import type { LoginResultDTO, MeDTO, SignupInput } from '@dezza/shared';
import { api, ApiError, apiEvents } from './api';
import { tokenStore } from './platform';

interface AuthContextValue {
  me: MeDTO | null;
  isLoading: boolean;
  /** A API não respondeu (rede ou servidor fora) e não há sessão em cache: não é o mesmo que "deslogado". */
  unreachable: boolean;
  login: (email: string, password: string) => Promise<LoginResultDTO>;
  verifyMfa: (challengeToken: string, code: string) => Promise<LoginResultDTO>;
  signup: (input: SignupInput) => Promise<LoginResultDTO>;
  logout: () => Promise<void>;
  switchCompany: (companyId: string) => Promise<void>;
  refresh: () => Promise<void>;
  applySession: (result: LoginResultDTO) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

async function fetchMe(): Promise<MeDTO | null> {
  try {
    return await api.get<MeDTO>('/auth/me', undefined, { silent: true });
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return null;
    throw err;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ['me'],
    queryFn: fetchMe,
    staleTime: 60_000,
    // A sessão restaurada do cache (modo offline) é sempre revalidada ao abrir o app.
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    meta: { persist: true },
  });

  useEffect(() => {
    const onUnauth = () => {
      tokenStore.set(null);
      qc.setQueryData(['me'], null);
    };
    apiEvents.addEventListener('unauthenticated', onUnauth);
    return () => apiEvents.removeEventListener('unauthenticated', onUnauth);
  }, [qc]);

  const applySession = useCallback(
    (result: LoginResultDTO) => {
      if (result.token) tokenStore.set(result.token);
      if (result.me) qc.setQueryData(['me'], result.me);
    },
    [qc],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      me: query.data ?? null,
      // isPending (e não isLoading): enquanto o cache local é restaurado a consulta ainda não "busca", e
      // isLoading ficava falso. Isso mandava a pessoa logada para /entrar por um instante (tela em branco).
      isLoading: query.isPending,
      unreachable: query.isError && query.data == null,
      applySession,
      async login(email, password) {
        const r = await api.post<LoginResultDTO>('/auth/login', { email, password });
        if (!r.mfaRequired) {
          qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' });
          applySession(r);
        }
        return r;
      },
      async verifyMfa(challengeToken, code) {
        const r = await api.post<LoginResultDTO>('/auth/mfa', { challengeToken, code });
        qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' });
        applySession(r);
        return r;
      },
      async signup(input) {
        const r = await api.post<LoginResultDTO>('/auth/signup', input);
        applySession(r);
        return r;
      },
      async logout() {
        await api.post('/auth/logout').catch(() => undefined);
        tokenStore.set(null);
        qc.clear();
        qc.setQueryData(['me'], null);
      },
      async switchCompany(companyId) {
        await api.post('/auth/switch-company', { companyId });
        qc.clear();
        await qc.fetchQuery({ queryKey: ['me'], queryFn: fetchMe });
      },
      async refresh() {
        await qc.invalidateQueries({ queryKey: ['me'] });
      },
    }),
    [query.data, query.isPending, query.isError, qc, applySession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth precisa do <AuthProvider>');
  return ctx;
}

/** Dados da sessão garantidamente presentes (use apenas dentro de rotas protegidas). */
export function useMe(): MeDTO & { company: NonNullable<MeDTO['company']>; membership: NonNullable<MeDTO['membership']>; entitlements: NonNullable<MeDTO['entitlements']> } {
  const { me } = useAuth();
  if (!me || !me.company || !me.membership || !me.entitlements) throw new Error('useMe fora de rota protegida');
  return me as ReturnType<typeof useMe>;
}

/** Redireciona usuários já logados (telas de login/cadastro). */
export function RedirectIfAuthed({ children }: { children: ReactNode }) {
  const { me } = useAuth();
  const location = useLocation();
  // Não espera a sessão: o formulário aparece na hora e, se houver sessão, segue para o app.
  if (me) {
    const next = new URLSearchParams(location.search).get('next');
    return <Navigate to={next && next.startsWith('/') && !next.startsWith('//') ? next : '/inicio'} replace />;
  }
  return <>{children}</>;
}
